// eslint-disable-next-line no-restricted-imports
import { useFieldArray as useFieldArrayRHF } from 'react-hook-form';

import type { TypedFieldName } from './type';
import { useField } from './useField';

type FieldValueWithId<FieldValue> = FieldValue & { id: string };
/**
 * The return type of the useFieldArray hook.
 * @template FieldValue The type of the field value.
 */
type ReturnType<FieldValue> = {
    /** Removes a field at the specified index. */
    remove: (index: number) => void;
    /** Pushes a new item to the field array. */
    push: (item: FieldValue) => void;
    /** The array of fields with their values and ids. */
    fields: FieldValueWithId<FieldValue>[];
    /** The error message, if any. */
    error?: string;
    /** Notifies about the update */
    update: (index: number, value: FieldValue) => void;
};

type FieldOptions = {
    /** Whether to clear the field when the component unmounts. */
    shouldClearOnUnmount?: boolean;
};

/**
 * A custom hook for managing an array of form fields.
 * @template FieldValue The type of the field value.
 * @template FormValues The type of the overall form values.
 * @param {TypedFieldName<FormValues>} name The name of the field array.
 * @param {FieldOptions} [options] Optional configuration for the field array.
 * @returns {ReturnType<FieldValue>} An object with methods to manipulate the field array and its current state.
 */
export const useFieldArray = <FieldValue = unknown, FormValues = void>(
    name: TypedFieldName<FormValues>,
    options?: FieldOptions,
): ReturnType<FieldValue> => {
    const {
        remove: removeRTL,
        append,
        fields,
        update: updateRTL,
    } = useFieldArrayRHF({ name, shouldUnregister: options?.shouldClearOnUnmount });
    const [, { error }] = useField(name);

    function remove(index: number): void {
        removeRTL(index);
    }

    function push(item: FieldValue): void {
        // @ts-expect-error mismatch of type between the layer and RHF
        append(item);
    }

    function update(index: number, item: FieldValue): void {
        // @ts-expect-error mismatch of type between the layer and RHF
        updateRTL(index, item);
    }

    return { remove, push, update, fields: fields as FieldValueWithId<FieldValue>[], error };
};
